import { CheckIcon } from "@solomon-labs/design/components/icons";
import { Text } from "@solomon-labs/design/components/ui";
import type React from "react";

import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { Spinner } from "../ui/spinner";

/**
 * @scope console
 *
 * `AccountOwnershipVerificationPanel` — inline panel rendering the
 * account-ownership verification flow (sign-message or
 * micro-transfer-fallback method) as a series of states the user
 * progresses through inside their current page surface. Replaces the
 * legacy modal pop-up shape; verification is a wizard step now, not an
 * interrupt.
 *
 * Consumers:
 * - EditPosition/InlinePositionEditor — verify-before-edit-rewards
 *
 * The panel is **presentational and stateless** — it renders whatever
 * state the consumer passes in. The state machine itself lives in
 * `useVerifyActionFlow` (the hook); consumers drive that hook
 * and pass `step` / `error` / `messageToSign` / etc. through to the
 * panel.
 *
 * The panel does NOT:
 * - Open as a modal (it's inline; consumers render it inside their
 *   page or right-pane surface).
 * - Handle wallet-not-connected state — that's a wrapper concern. The
 *   consumer should render a Connect-Wallet prompt instead of this
 *   panel when `!connected`.
 * - Render the cost-disclosure step's content as a separate modal —
 *   instead, the disclosure is a state inside this same panel
 *   (matches the "no pop-up except destructive confirms" pattern).
 *
 * The retail-yaas variant (when it exists) will likely fork into a
 * sibling component — different state shape, different entry point,
 * different copy. Don't grow this one to handle both.
 */

export type VerificationStep =
    | "walletSign"
    | "walletApprove"
    | "confirming"
    | "committing"
    | "success"
    | "error"
    | "costDisclosure";

export type VerificationMethod = "signMessage" | "microTransfer";

/**
 * Why the flow landed on the error step. Drives the calm, non-alarming
 * error copy and — crucially — the CTA hierarchy:
 * - `cancelled`: the user dismissed the wallet prompt. Retrying the same
 *   method is the right primary action.
 * - everything else (`couldNotSign` / `noResponse` / `transferFailed`):
 *   for a signMessage attempt the working path is the micro-transfer, so
 *   that becomes the primary action and "try signing again" is secondary.
 * This is a built-in fallback, not a platform error, so no "something went
 * wrong" framing.
 */
export type VerificationErrorKind =
    | "cancelled"
    | "couldNotSign"
    | "noResponse"
    | "transferFailed";

export interface VerificationCopy {
    /** Small uppercase eyebrow at the top of the title stack. */
    eyebrow: string;
    /** Title for the walletSign step (primary action-specific copy). */
    walletSignTitle: string;
    /** Body text under walletSign. */
    walletSignBody?: string;
    /** Title for the committing step. */
    committingTitle: string;
    /** Body text under committing. */
    committingBody: string;
    /** Title for the success step. */
    successTitle: string;
    /** One-line success summary shown next to the check icon. */
    successSummary: string;
    /** Optional helper line under the success summary. */
    successFootnote?: string;
}

export interface AccountOwnershipVerificationPanelProps {
    step: VerificationStep;
    verificationMethod: VerificationMethod | null;
    error: string | null;
    /** Why the flow failed (drives error-step copy + CTA hierarchy). */
    errorKind?: VerificationErrorKind | null;
    /** Literal message the wallet is being asked to sign (signMessage method). */
    messageToSign: string | null;
    /** Human-readable summary of what is being signed, shown on the walletSign
     *  step in place of the raw `messageToSign` echo. The wallet's own popup
     *  still shows the literal bytes; this is purely a friendlier in-app
     *  rendering (e.g. pool name + truncated addresses instead of the raw
     *  template with a pool UUID). Falls back to `messageToSign` when omitted. */
    signSummary?: React.ReactNode;
    copy: VerificationCopy;
    /** Continue label on the success step. Defaults to "Continue". */
    successCtaLabel?: string;
    /** Optional secondary action shown beside Continue on the success step. */
    successSecondaryCtaLabel?: string;
    onSuccessSecondaryAction?: () => void;
    onRetry: () => void;
    /** Called when the user agrees to try the micro-transfer fallback
     *  from the error step. The consumer should advance the hook to
     *  `costDisclosure`. */
    onTryMicroTransfer: () => void;
    /** Called when the user confirms the cost on the cost-disclosure
     *  step. The consumer should re-run the flow with method
     *  "microTransfer". */
    onConfirmMicroTransfer: () => void;
    /** Called when the user dismisses the cost-disclosure step
     *  (returns to the error step). */
    onCancelMicroTransfer: () => void;
    /** Called when the user clicks Continue on the success step. */
    onContinue: () => void;
    /** Suppresses the micro-transfer cost-disclosure / fallback affordance.
     *  Use when the consumer's verification path doesn't support a
     *  micro-transfer fallback. */
    microTransferDisabled?: boolean;
    /** Optional escape hatch shown beneath the "Waiting on wallet…" spinner
     *  during the walletSign step. Lets the user bail out of a wallet that
     *  never responds (locked, or an invalidated extension context) instead
     *  of being stuck on the spinner. When omitted, no cancel button renders. */
    onCancelWaiting?: () => void;
    /** Label for the cancel-waiting button. Defaults to "Cancel". */
    cancelWaitingLabel?: string;
    /** Optional className applied to the outer Card. */
    className?: string;
}

export function AccountOwnershipVerificationPanel({
    step,
    verificationMethod,
    error,
    errorKind,
    messageToSign,
    signSummary,
    copy,
    successCtaLabel = "Continue",
    successSecondaryCtaLabel,
    onSuccessSecondaryAction,
    onRetry,
    onTryMicroTransfer,
    onConfirmMicroTransfer,
    onCancelMicroTransfer,
    onContinue,
    microTransferDisabled = false,
    onCancelWaiting,
    cancelWaitingLabel = "Cancel",
    className
}: AccountOwnershipVerificationPanelProps): React.ReactElement {
    return (
        <Card
            rounded={false}
            className={className ?? "p-6 flex flex-col gap-5 max-w-[640px]"}
        >
            {step === "walletSign" && (
                <WalletSignContent
                    method={verificationMethod ?? "signMessage"}
                    messageToSign={messageToSign}
                    signSummary={signSummary}
                    copy={copy}
                    onCancel={onCancelWaiting}
                    cancelLabel={cancelWaitingLabel}
                />
            )}
            {step === "walletApprove" && <WalletApproveContent copy={copy} />}
            {step === "confirming" && <ConfirmingContent copy={copy} />}
            {step === "committing" && <CommittingContent copy={copy} />}
            {step === "success" && (
                <SuccessContent
                    copy={copy}
                    ctaLabel={successCtaLabel}
                    secondaryCtaLabel={successSecondaryCtaLabel}
                    onContinue={onContinue}
                    onSecondaryAction={onSuccessSecondaryAction}
                />
            )}
            {step === "error" && (
                <ErrorContent
                    error={error}
                    errorKind={errorKind ?? null}
                    verificationMethod={verificationMethod}
                    copy={copy}
                    onRetry={onRetry}
                    onTryMicroTransfer={onTryMicroTransfer}
                    microTransferDisabled={microTransferDisabled}
                />
            )}
            {step === "costDisclosure" && !microTransferDisabled && (
                <CostDisclosureContent
                    onConfirm={onConfirmMicroTransfer}
                    onCancel={onCancelMicroTransfer}
                />
            )}
        </Card>
    );
}

/* ── Step content ────────────────────────────────────────── */

function StepHeader({
    eyebrow,
    title
}: {
    eyebrow: string;
    title: string;
}): React.ReactElement {
    return (
        <div className="flex flex-col gap-1">
            <Text variant="label" className="text-foreground-muted">
                {eyebrow}
            </Text>
            <Text
                variant="h3"
                render={<span />}
                className="text-foreground-base"
            >
                {title}
            </Text>
        </div>
    );
}

function WalletSignContent({
    method,
    messageToSign,
    signSummary,
    copy,
    onCancel,
    cancelLabel
}: {
    method: VerificationMethod;
    messageToSign: string | null;
    signSummary?: React.ReactNode;
    copy: VerificationCopy;
    onCancel?: () => void;
    cancelLabel: string;
}): React.ReactElement {
    return (
        <>
            <StepHeader eyebrow={copy.eyebrow} title={copy.walletSignTitle} />
            <Text
                variant="body"
                className="text-foreground-muted leading-relaxed"
            >
                {method === "microTransfer"
                    ? "Approve the test transfer in your wallet. It moves 0.000001 SOL from your wallet to Solomon's verification address."
                    : (copy.walletSignBody ??
                      "This proves you own the wallet. No transaction is sent and no funds move.")}
            </Text>
            {method === "signMessage" &&
                (signSummary ? (
                    <div className="flex flex-col gap-2 bg-surface-sunken border border-line-base px-3 py-3">
                        {signSummary}
                    </div>
                ) : messageToSign ? (
                    <Text
                        variant="number"
                        render={<pre />}
                        className="text-foreground-base bg-surface-sunken border border-line-base rounded-none px-3 py-2 whitespace-pre-wrap break-words"
                    >
                        {messageToSign}
                    </Text>
                ) : null)}
            {onCancel ? (
                <div className="flex items-center justify-between gap-3">
                    <PendingRow label="Waiting on wallet…" />
                    <Button
                        variant="transparent"
                        size="sm"
                        interaction="underline"
                        onClick={onCancel}
                    >
                        {cancelLabel}
                    </Button>
                </div>
            ) : (
                <PendingRow label="Waiting on wallet…" />
            )}
        </>
    );
}

function WalletApproveContent({
    copy
}: {
    copy: VerificationCopy;
}): React.ReactElement {
    return (
        <>
            <StepHeader eyebrow={copy.eyebrow} title="Waiting for approval." />
            <Text
                variant="body"
                className="text-foreground-muted leading-relaxed"
            >
                Confirm the transfer in your wallet. We&rsquo;ll detect it
                automatically once it&rsquo;s broadcast.
            </Text>
            <PendingRow label="Awaiting wallet confirmation…" />
        </>
    );
}

function ConfirmingContent({
    copy
}: {
    copy: VerificationCopy;
}): React.ReactElement {
    return (
        <>
            <StepHeader eyebrow={copy.eyebrow} title="Confirming…" />
            <Text
                variant="body"
                className="text-foreground-muted leading-relaxed"
            >
                Waiting for the transaction to confirm on-chain.
            </Text>
            <PendingRow label="Confirming on-chain…" />
        </>
    );
}

function CommittingContent({
    copy
}: {
    copy: VerificationCopy;
}): React.ReactElement {
    return (
        <>
            <StepHeader eyebrow={copy.eyebrow} title={copy.committingTitle} />
            <Text
                variant="body"
                className="text-foreground-muted leading-relaxed"
            >
                {copy.committingBody}
            </Text>
            <PendingRow label="Finalizing…" />
        </>
    );
}

function SuccessContent({
    copy,
    ctaLabel,
    secondaryCtaLabel,
    onContinue,
    onSecondaryAction
}: {
    copy: VerificationCopy;
    ctaLabel: string;
    secondaryCtaLabel?: string;
    onContinue: () => void;
    onSecondaryAction?: () => void;
}): React.ReactElement {
    return (
        <>
            <StepHeader eyebrow={copy.eyebrow} title={copy.successTitle} />
            <div className="flex items-center gap-3">
                <CheckIcon className="text-accent-base size-5" />
                <Text
                    variant="body"
                    render={<span />}
                    className="text-foreground-base"
                >
                    {copy.successSummary}
                </Text>
            </div>
            {copy.successFootnote && (
                <Text
                    variant="small"
                    className="text-foreground-muted leading-relaxed"
                >
                    {copy.successFootnote}
                </Text>
            )}
            <div className="flex items-center gap-3">
                <Button
                    variant="raised"
                    interaction="lifted"
                    onClick={onContinue}
                >
                    {ctaLabel}
                </Button>
                {secondaryCtaLabel && onSecondaryAction && (
                    <Button
                        variant="transparent"
                        interaction="underline"
                        onClick={onSecondaryAction}
                    >
                        {secondaryCtaLabel}
                    </Button>
                )}
            </div>
        </>
    );
}

const ERROR_TITLES: Record<VerificationErrorKind, string> = {
    cancelled: "Signature cancelled.",
    couldNotSign: "Let's verify a different way.",
    noResponse: "Your wallet didn't respond.",
    transferFailed: "That transfer didn't go through."
};

/**
 * Error step. This is a built-in fallback flow, so it stays calm: it reuses
 * the section's neutral eyebrow (no red "Verification failed" / "Something
 * went wrong"), and it leads with whichever action will actually work.
 *
 * For a signMessage attempt that wasn't a deliberate cancel (hardware
 * wallets, no response), the micro-transfer is the working path, so it
 * becomes the primary CTA. A genuine cancel keeps "Try again" primary.
 */
function ErrorContent({
    error,
    errorKind,
    verificationMethod,
    copy,
    onRetry,
    onTryMicroTransfer,
    microTransferDisabled
}: {
    error: string | null;
    errorKind: VerificationErrorKind | null;
    verificationMethod: VerificationMethod | null;
    copy: VerificationCopy;
    onRetry: () => void;
    onTryMicroTransfer: () => void;
    microTransferDisabled: boolean;
}): React.ReactElement {
    const leadWithTransfer =
        verificationMethod === "signMessage" &&
        errorKind !== "cancelled" &&
        !microTransferDisabled;
    const showTransferSecondary =
        !leadWithTransfer &&
        !microTransferDisabled &&
        verificationMethod === "signMessage";
    const title = ERROR_TITLES[errorKind ?? "couldNotSign"];

    return (
        <>
            <StepHeader eyebrow={copy.eyebrow} title={title} />
            <Text
                variant="body"
                className="text-foreground-muted leading-relaxed"
            >
                {error ??
                    "We couldn't get a signature from your wallet. You can try again, or verify with a tiny test transfer instead."}
            </Text>
            <div className="flex flex-col gap-2 self-start">
                {leadWithTransfer ? (
                    <>
                        <Button
                            variant="raised"
                            interaction="lifted"
                            onClick={onTryMicroTransfer}
                        >
                            Verify with a test transfer
                        </Button>
                        <Button
                            variant="transparent"
                            interaction="underline"
                            onClick={onRetry}
                        >
                            Try signing again
                        </Button>
                    </>
                ) : (
                    <>
                        <Button
                            variant="raised"
                            interaction="lifted"
                            onClick={onRetry}
                        >
                            Try again
                        </Button>
                        {showTransferSecondary && (
                            <Button
                                variant="transparent"
                                interaction="underline"
                                onClick={onTryMicroTransfer}
                            >
                                Verify with a test transfer instead
                            </Button>
                        )}
                    </>
                )}
            </div>
        </>
    );
}

function CostDisclosureContent({
    onConfirm,
    onCancel
}: {
    onConfirm: () => void;
    onCancel: () => void;
}): React.ReactElement {
    return (
        <>
            <StepHeader
                eyebrow="Fallback verification"
                title="Verify via a tiny test transfer."
            />
            <Text
                variant="body"
                className="text-foreground-muted leading-relaxed"
            >
                This will cost about <Text variant="number">$0.01</Text> (
                <Text variant="number">0.000001&nbsp;SOL</Text> + network fee).
                We send it to Solomon&rsquo;s verification address and read the
                signature. OK to proceed?
            </Text>
            <div className="flex flex-col gap-2 self-start">
                <Button
                    variant="raised"
                    interaction="lifted"
                    onClick={onConfirm}
                >
                    OK, proceed
                </Button>
                <Button
                    variant="transparent"
                    interaction="underline"
                    onClick={onCancel}
                >
                    Cancel
                </Button>
            </div>
        </>
    );
}

/* ── Primitives ──────────────────────────────────────────── */

function PendingRow({ label }: { label: string }): React.ReactElement {
    return (
        <div className="flex items-center gap-3">
            <Spinner size="sm" />
            <Text
                variant="small"
                render={<span />}
                className="text-foreground-muted"
            >
                {label}
            </Text>
        </div>
    );
}
